import React, { useState, useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Trophy, Star, Gift, Trash, Plus, Users, Coins, ArrowUp, CircleNotch,
  Lightning, Calendar, Crown, MagnifyingGlass, PencilSimple
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoyaltySettingsPage = () => {
  const { currentStore } = useStore();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('config');

  // Top clients
  const [topClients, setTopClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Bonus events
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', multiplier: 2, start_date: '', end_date: '', is_active: true });

  // Manual adjust
  const [adjustEmail, setAdjustEmail] = useState('');
  const [adjustPoints, setAdjustPoints] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    if (!currentStore) return;
    Promise.all([
      axios.get(`${API_URL}/api/stores/${currentStore.id}/loyalty/config`, { withCredentials: true }),
      axios.get(`${API_URL}/api/stores/${currentStore.id}/loyalty/stats`, { withCredentials: true }),
    ]).then(([configResp, statsResp]) => {
      setConfig(configResp.data);
      setStats(statsResp.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [currentStore]);

  useEffect(() => {
    if (!currentStore || tab !== 'clients') return;
    setLoadingClients(true);
    axios.get(`${API_URL}/api/stores/${currentStore.id}/loyalty/top-clients`, { withCredentials: true })
      .then(r => setTopClients(r.data))
      .catch(() => {})
      .finally(() => setLoadingClients(false));
  }, [currentStore, tab]);

  useEffect(() => {
    if (!currentStore || tab !== 'events') return;
    setLoadingEvents(true);
    axios.get(`${API_URL}/api/stores/${currentStore.id}/loyalty/events`, { withCredentials: true })
      .then(r => setEvents(r.data))
      .catch(() => {})
      .finally(() => setLoadingEvents(false));
  }, [currentStore, tab]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${currentStore.id}/loyalty/config`, config, { withCredentials: true });
      toast.success('Configuration fidelite sauvegardee');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const updateTier = (index, field, value) => {
    const tiers = [...(config.tiers || [])];
    tiers[index] = { ...tiers[index], [field]: value };
    setConfig({ ...config, tiers });
  };

  const addTier = () => {
    const tiers = [...(config.tiers || [])];
    tiers.push({ name: 'Nouveau palier', min_points: 1500, discount_percent: 10, color: '#000' });
    setConfig({ ...config, tiers });
  };

  const removeTier = (index) => {
    const tiers = [...(config.tiers || [])];
    tiers.splice(index, 1);
    setConfig({ ...config, tiers });
  };

  const updateReward = (index, field, value) => {
    const rewards = [...(config.rewards || [])];
    rewards[index] = { ...rewards[index], [field]: value };
    setConfig({ ...config, rewards });
  };

  const addReward = () => {
    const rewards = [...(config.rewards || [])];
    rewards.push({ name: 'Nouvelle recompense', points_cost: 300, discount_value: 15, type: 'fixed' });
    setConfig({ ...config, rewards });
  };

  const removeReward = (index) => {
    const rewards = [...(config.rewards || [])];
    rewards.splice(index, 1);
    setConfig({ ...config, rewards });
  };

  const handleCreateEvent = async () => {
    if (!newEvent.name || !newEvent.start_date || !newEvent.end_date) {
      toast.error('Remplissez tous les champs');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/stores/${currentStore.id}/loyalty/events`, newEvent, { withCredentials: true });
      toast.success('Evenement bonus cree');
      setNewEvent({ name: '', multiplier: 2, start_date: '', end_date: '', is_active: true });
      const r = await axios.get(`${API_URL}/api/stores/${currentStore.id}/loyalty/events`, { withCredentials: true });
      setEvents(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API_URL}/api/stores/${currentStore.id}/loyalty/events/${eventId}`, { withCredentials: true });
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Evenement supprime');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleAdjustPoints = async () => {
    if (!adjustEmail || !adjustPoints) {
      toast.error('Email et points requis');
      return;
    }
    setAdjusting(true);
    try {
      const resp = await axios.post(`${API_URL}/api/stores/${currentStore.id}/loyalty/adjust-points`, {
        email: adjustEmail,
        points: parseInt(adjustPoints),
        reason: adjustReason || 'Ajustement manuel',
      }, { withCredentials: true });
      toast.success(resp.data.message);
      setAdjustEmail('');
      setAdjustPoints('');
      setAdjustReason('');
      // Refresh top clients if on that tab
      if (tab === 'clients') {
        const r = await axios.get(`${API_URL}/api/stores/${currentStore.id}/loyalty/top-clients`, { withCredentials: true });
        setTopClients(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setAdjusting(false); }
  };

  if (!currentStore) return null;

  const tabs = [
    { key: 'config', label: 'Configuration', icon: Star },
    { key: 'clients', label: 'Top clients', icon: Crown },
    { key: 'events', label: 'Bonus', icon: Lightning },
    { key: 'adjust', label: 'Ajustement', icon: PencilSimple },
  ];

  return (
    <div className="space-y-6" data-testid="loyalty-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Programme de fidelite</h1>
        <p className="text-slate-500 mt-1">Recompensez vos clients reguliers pour les fideliser</p>
      </div>

      {loading ? (
        <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF] mx-auto" /></div>
      ) : config && (
        <>
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Membres', value: stats.total_members, icon: Users, color: '#0052FF' },
                { label: 'Points en circulation', value: stats.total_points_outstanding, icon: Coins, color: '#c2185b' },
                { label: 'Points distribues', value: stats.total_points_earned, icon: ArrowUp, color: '#16a34a' },
                { label: 'Points echanges', value: stats.total_points_redeemed, icon: Gift, color: '#d97706' },
              ].map((s, i) => (
                <Card key={i} className="border-0 shadow-sm">
                  <CardContent className="p-3 text-center">
                    <s.icon size={18} style={{ color: s.color }} className="mx-auto mb-1" weight="fill" />
                    <p className="text-lg font-bold">{s.value?.toLocaleString()}</p>
                    <p className="text-[10px] text-slate-400">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium transition-all ${
                  tab === t.key ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`loyalty-tab-${t.key}`}
              >
                <t.icon size={15} weight={tab === t.key ? 'fill' : 'regular'} />
                {t.label}
              </button>
            ))}
          </div>

          {/* ── CONFIG TAB ── */}
          {tab === 'config' && (
            <div className="space-y-5">
              {/* General Config */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><Star size={18} /> Configuration generale</CardTitle>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500">Activer</Label>
                      <Switch checked={config.enabled} onCheckedChange={v => setConfig({ ...config, enabled: v })} data-testid="loyalty-enabled" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Points par euro depense</Label>
                      <Input type="number" value={config.points_per_euro} onChange={e => setConfig({ ...config, points_per_euro: parseInt(e.target.value) || 1 })} data-testid="points-per-euro" />
                    </div>
                    <div>
                      <Label className="text-xs">Bonus de bienvenue (points)</Label>
                      <Input type="number" value={config.welcome_bonus} onChange={e => setConfig({ ...config, welcome_bonus: parseInt(e.target.value) || 0 })} data-testid="welcome-bonus" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tiers */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><Trophy size={18} /> Paliers</CardTitle>
                    <Button variant="outline" size="sm" onClick={addTier} className="text-xs" data-testid="add-tier-btn"><Plus size={12} className="mr-1" /> Ajouter</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {config.tiers?.map((tier, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`tier-${i}`}>
                      <input type="color" value={tier.color} onChange={e => updateTier(i, 'color', e.target.value)} className="w-8 h-8 rounded-full cursor-pointer border-0" />
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <Input value={tier.name} onChange={e => updateTier(i, 'name', e.target.value)} placeholder="Nom" className="text-sm h-8" />
                        <Input type="number" value={tier.min_points} onChange={e => updateTier(i, 'min_points', parseInt(e.target.value) || 0)} placeholder="Points min" className="text-sm h-8" />
                        <Input type="number" value={tier.discount_percent} onChange={e => updateTier(i, 'discount_percent', parseInt(e.target.value) || 0)} placeholder="% remise" className="text-sm h-8" />
                      </div>
                      {i > 0 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => removeTier(i)} data-testid={`remove-tier-${i}`}><Trash size={14} /></Button>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Rewards */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><Gift size={18} /> Recompenses</CardTitle>
                    <Button variant="outline" size="sm" onClick={addReward} className="text-xs" data-testid="add-reward-btn"><Plus size={12} className="mr-1" /> Ajouter</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {config.rewards?.map((reward, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`reward-config-${i}`}>
                      <div className="flex-1 grid grid-cols-4 gap-2">
                        <Input value={reward.name} onChange={e => updateReward(i, 'name', e.target.value)} placeholder="Nom" className="text-sm h-8 col-span-2" />
                        <Input type="number" value={reward.points_cost} onChange={e => updateReward(i, 'points_cost', parseInt(e.target.value) || 0)} placeholder="Cout pts" className="text-sm h-8" />
                        <select value={reward.type} onChange={e => updateReward(i, 'type', e.target.value)} className="text-sm h-8 border rounded-md px-2">
                          <option value="fixed">Reduction EUR</option>
                          <option value="percentage">Reduction %</option>
                          <option value="free_shipping">Livraison offerte</option>
                        </select>
                      </div>
                      {(reward.type === 'fixed' || reward.type === 'percentage') && (
                        <Input type="number" value={reward.discount_value} onChange={e => updateReward(i, 'discount_value', parseFloat(e.target.value) || 0)} placeholder="Valeur" className="text-sm h-8 w-24" />
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => removeReward(i)} data-testid={`remove-reward-${i}`}><Trash size={14} /></Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF]" data-testid="loyalty-save-btn">
                {saving ? <CircleNotch className="animate-spin mr-2" size={14} /> : <Star className="mr-2" size={14} />}
                Sauvegarder la configuration
              </Button>
            </div>
          )}

          {/* ── TOP CLIENTS TAB ── */}
          {tab === 'clients' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Crown size={18} weight="fill" className="text-amber-500" /> Classement fidelite</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingClients ? (
                  <div className="py-8 text-center"><CircleNotch size={24} className="animate-spin mx-auto text-slate-400" /></div>
                ) : topClients.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">Aucun membre fidelite</p>
                ) : (
                  <div className="space-y-2">
                    {topClients.map((client, i) => (
                      <div key={client.email} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`top-client-${i}`}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: i < 3 ? ['#FFD700', '#C0C0C0', '#CD7F32'][i] : '#94a3b8' }}>
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium truncate">{client.email}</p>
                          <div className="flex items-center gap-2 text-[11px] text-slate-400">
                            <span>{client.total_orders} cmd</span>
                            <span>{client.total_order_amount?.toFixed(0)} EUR</span>
                          </div>
                        </div>
                        <Badge className="text-[10px] px-2 py-0.5" style={{ backgroundColor: client.tier_color + '20', color: client.tier_color, borderColor: client.tier_color }}>
                          {client.tier_name}
                        </Badge>
                        <div className="text-right">
                          <p className="text-[14px] font-bold">{client.points?.toLocaleString()}</p>
                          <p className="text-[10px] text-slate-400">pts dispo</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── BONUS EVENTS TAB ── */}
          {tab === 'events' && (
            <div className="space-y-5">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><Lightning size={18} weight="fill" className="text-yellow-500" /> Creer un evenement bonus</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Nom de l'evenement</Label>
                      <Input value={newEvent.name} onChange={e => setNewEvent({ ...newEvent, name: e.target.value })} placeholder="Ex: Double points weekend" className="text-sm" data-testid="event-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Multiplicateur</Label>
                      <Input type="number" step="0.5" min="1.5" value={newEvent.multiplier} onChange={e => setNewEvent({ ...newEvent, multiplier: parseFloat(e.target.value) || 2 })} className="text-sm" data-testid="event-multiplier" />
                    </div>
                    <div>
                      <Label className="text-xs">Date debut</Label>
                      <Input type="datetime-local" value={newEvent.start_date} onChange={e => setNewEvent({ ...newEvent, start_date: e.target.value })} className="text-sm" data-testid="event-start" />
                    </div>
                    <div>
                      <Label className="text-xs">Date fin</Label>
                      <Input type="datetime-local" value={newEvent.end_date} onChange={e => setNewEvent({ ...newEvent, end_date: e.target.value })} className="text-sm" data-testid="event-end" />
                    </div>
                  </div>
                  <Button onClick={handleCreateEvent} className="bg-amber-500 hover:bg-amber-600 text-sm" data-testid="create-event-btn">
                    <Lightning size={14} className="mr-1" weight="fill" /> Creer l'evenement
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Evenements existants</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingEvents ? (
                    <div className="py-6 text-center"><CircleNotch size={24} className="animate-spin mx-auto text-slate-400" /></div>
                  ) : events.length === 0 ? (
                    <p className="text-center text-slate-400 py-6">Aucun evenement bonus</p>
                  ) : (
                    <div className="space-y-2">
                      {events.map((event) => {
                        const now = new Date();
                        const start = new Date(event.start_date);
                        const end = new Date(event.end_date);
                        const isActive = event.is_active && now >= start && now <= end;
                        return (
                          <div key={event.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`event-${event.id}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${isActive ? 'bg-amber-500' : 'bg-slate-300'}`}>
                              x{event.multiplier}
                            </div>
                            <div className="flex-1">
                              <p className="text-[13px] font-medium">{event.name}</p>
                              <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                                <Calendar size={10} />
                                <span>{new Date(event.start_date).toLocaleDateString('fr-FR')} - {new Date(event.end_date).toLocaleDateString('fr-FR')}</span>
                                {isActive && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] px-1.5 py-0">ACTIF</Badge>}
                              </div>
                            </div>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDeleteEvent(event.id)}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── MANUAL ADJUST TAB ── */}
          {tab === 'adjust' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><PencilSimple size={18} /> Ajustement manuel de points</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-[12px] text-slate-500">Ajoutez ou retirez des points manuellement pour un client.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs">Email du client</Label>
                    <Input value={adjustEmail} onChange={e => setAdjustEmail(e.target.value)} placeholder="client@example.com" className="text-sm" data-testid="adjust-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Points (positif ou negatif)</Label>
                    <Input type="number" value={adjustPoints} onChange={e => setAdjustPoints(e.target.value)} placeholder="Ex: 100 ou -50" className="text-sm" data-testid="adjust-points" />
                  </div>
                  <div>
                    <Label className="text-xs">Raison</Label>
                    <Input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Geste commercial" className="text-sm" data-testid="adjust-reason" />
                  </div>
                </div>
                <Button onClick={handleAdjustPoints} disabled={adjusting} className="bg-[#0052FF]" data-testid="adjust-submit-btn">
                  {adjusting ? <CircleNotch className="animate-spin mr-2" size={14} /> : <PencilSimple className="mr-2" size={14} />}
                  Ajuster les points
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default LoyaltySettingsPage;
